/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArchiveIcon,
  CheckCircledIcon,
  DotsVerticalIcon,
  EyeNoneIcon,
  ResetIcon,
  TrashIcon,
} from '@radix-ui/react-icons';
import { Menu, Text, UnstyledButton } from '@/shared/ui';
import { cn } from '@/shared/ui/utils/tailwind';
import { PropertyDialog } from './PropertyDialog';

/** One entry in the actions menu. */
interface ActionEntry {
  id: string;
  label: string;
  icon: React.ReactNode;
  run: () => void;
  /** Painted in the error colour and separated from the rest. */
  destructive?: boolean;
  /** Shown in a dialog before the action runs. Absent runs immediately. */
  confirm?: { title: string; body: string; confirmLabel: string };
}

interface PropertyActionsMenuProps {
  /** `classification.status`; decides which transitions are on offer. */
  status: string;
  saving: boolean;
  onSuspend: () => void;
  onMarkSold: () => void;
  onArchive: () => void;
  /** Puts a suspended, sold or archived listing back on the market. */
  onReactivate: () => void;
  onDelete: () => void;
  /**
   * Sizes the trigger for the mobile detail header: 44px and borderless,
   * matching the two actions beside it. A 38px bordered button in that row
   * reads as a mistake long before anybody works out which of the three is
   * the wrong one.
   */
  inHeader?: boolean;
}

/**
 * Statuses a listing cannot be edited out of, except by going back to
 * active. Mirrors `_TERMINAL_STATUSES` on the repository, which refuses an
 * ordinary write on any of them.
 */
const TERMINAL_STATUSES = ['inactive', 'suspended', 'sold', 'rented', 'archived'];

/** Which release label fits the state the listing is actually in. */
const RELEASE_LABEL_KEYS: Record<string, string> = {
  archived: 'detailView.actions.unarchive',
  suspended: 'detailView.actions.unsuspend',
};

const ICON = { width: 14, height: 14 } as const;

/**
 * The header's overflow menu: everything about the listing's lifecycle that
 * is not editing, sharing or scheduling.
 *
 * Every action here changes where the listing stands — sold, suspended,
 * archived, deleted — so every one of them asks first.
 *
 * @param props - Current state and the callbacks each action runs.
 * @returns The rendered trigger, menu and confirmation dialog.
 */
export const PropertyActionsMenu: React.FC<PropertyActionsMenuProps> = ({
  status,
  saving,
  onSuspend,
  onMarkSold,
  onArchive,
  onReactivate,
  onDelete,
  inHeader = false,
}) => {
  const { t } = useTranslation('features/properties');
  const [pending, setPending] = useState<ActionEntry | null>(null);

  // A listing that has left `active` refuses every write except the one
  // that releases it. Offering "Arquivar" on a listing that is already
  // archived produced a 404 and a toast blaming the save — the menu was
  // offering doors the backend had already locked.
  const isTerminal = TERMINAL_STATUSES.includes(status);

  const releaseEntry: ActionEntry = {
    id: 'reactivate',
    label: t(RELEASE_LABEL_KEYS[status] ?? 'detailView.actions.backToMarket'),
    icon: <ResetIcon {...ICON} />,
    // No confirmation: this is the undo, and the only way back.
    run: onReactivate,
  };

  const lifecycleEntries: ActionEntry[] = [
    {
      id: 'suspend',
      label: t('detailView.actions.suspend'),
      icon: <EyeNoneIcon {...ICON} />,
      run: onSuspend,
      confirm: {
        title: t('detailView.actions.suspend'),
        body: t('detailView.actions.suspendBody'),
        confirmLabel: t('detailView.actions.suspendConfirm'),
      },
    },
    {
      id: 'sold',
      label: t('detailView.actions.markSold'),
      // Not a star: that is the favourite toggle in the header, and the
      // same glyph for two unrelated things reads as one thing.
      icon: <CheckCircledIcon {...ICON} />,
      run: onMarkSold,
      confirm: {
        title: t('detailView.actions.markSold'),
        // The attendance pipeline writes `sold` on its own when a deal is
        // won; doing it by hand here is for a sale closed outside Housien.
        body: t('detailView.actions.markSoldBody'),
        confirmLabel: t('detailView.actions.markSoldConfirm'),
      },
    },
    {
      id: 'archive',
      label: t('detailView.actions.archive'),
      icon: <ArchiveIcon {...ICON} />,
      run: onArchive,
      confirm: {
        title: t('detailView.actions.archive'),
        body: t('detailView.actions.archiveBody'),
        confirmLabel: t('detailView.actions.archiveConfirm'),
      },
    },
  ];

  const deleteEntry: ActionEntry = {
    id: 'delete',
    label: t('detailView.actions.delete'),
    icon: <TrashIcon {...ICON} />,
    run: onDelete,
    destructive: true,
    confirm: {
      title: t('detailView.actions.delete'),
      body: t('detailView.actions.deleteBody'),
      confirmLabel: t('detailView.actions.deleteConfirm'),
    },
  };

  const entries: ActionEntry[] = isTerminal
    ? [releaseEntry, deleteEntry]
    : [...lifecycleEntries, deleteEntry];

  const handlePick = (entry: ActionEntry) => {
    if (entry.confirm) setPending(entry);
    else entry.run();
  };

  const handleConfirm = () => {
    pending?.run();
    setPending(null);
  };

  return (
    <>
      <Menu position="bottom-end" width={240}>
        <Menu.Target>
          <UnstyledButton
            type="button"
            data-testid="property-actions-menu"
            aria-label={t('detailView.actions.menuLabel')}
            title={t('detailView.actions.menuLabel')}
            disabled={saving}
            className={`flex flex-none items-center justify-center rounded-(--radius-md) text-[var(--text-secondary)] hover:bg-[var(--surface-hover)] hover:text-[var(--text-primary)] disabled:opacity-50 ${inHeader
              ? 'h-[44px] w-[44px]'
              : 'h-[38px] w-[38px] border border-solid border-[var(--border-medium)] bg-[var(--surface-primary)]'}`}
          >
            <DotsVerticalIcon width={16} height={16} />
          </UnstyledButton>
        </Menu.Target>
        <Menu.Dropdown className="p-0">
          {entries.map((entry) => (
            <Menu.Item
              key={entry.id}
              data-testid={`property-action-${entry.id}`}
              onClick={() => handlePick(entry)}
              className={cn(
                'items-center gap-(--space-2-5) rounded-none px-(--space-3) py-(--space-2-5)',
                entry.destructive && 'border-t border-(--border-ultra-subtle)',
              )}
            >
              <Text
                component="span"
                className={cn(
                  'flex shrink-0',
                  entry.destructive ? 'text-(--status-error-text)' : 'text-(--text-tertiary)',
                )}
              >
                {entry.icon}
              </Text>
              <Text
                component="span"
                className={cn(
                  'text-[length:var(--text-body-sm)] [font-weight:var(--font-medium)]',
                  entry.destructive ? 'text-(--status-error-text)' : 'text-(--text-primary)',
                )}
              >
                {entry.label}
              </Text>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>

      {pending?.confirm && (
        <PropertyDialog
          title={pending.confirm.title}
          width="440px"
          confirmLabel={pending.confirm.confirmLabel}
          onConfirm={handleConfirm}
          destructive={pending.destructive}
          onClose={() => setPending(null)}
          data-testid="property-action-confirm"
        >
          <Text className="m-0 text-[length:var(--text-body-sm)] leading-[var(--leading-relaxed)] text-[var(--text-primary)]">
            {pending.confirm.body}
          </Text>
        </PropertyDialog>
      )}
    </>
  );
};
